// To see list of smart car makes: https://smartcar.com/docs/api/?version=v1.0&language=Javascript#makes

import { remoteConfig } from '@/firebase'
import { useAppSelector } from '@/hooks'
import useMultiProgramFlowControl from '@/hooks/useMultiProgramFlowControl'
import {
  programsNewCollection,
  selectAvailablePrograms,
  selectProgram,
} from '@/reducers/programsNew'
import { match, P } from 'ts-pattern'
import { VehicleBrand } from './brand'
import { eligibleMakesCollection } from '@/reducers/eligibleMakes'

export function useVehicleMakes() {
  const { isLoadingOrIdle } = programsNewCollection.useFetch()
  const fromPartnerOnly = remoteConfig?.getBoolean(
    'back_soon_from_partner_only',
  )
  const backSoonBrands = remoteConfig?.getArray('back_soon_brands') ?? []
  const { data: eligibleMakes, isLoadingOrIdle: isLoadingEligibleMakes } =
    eligibleMakesCollection.useFetch()
  const stateConfiguredEligibleMakes = eligibleMakes.map((make) =>
    make.make.toLowerCase(),
  )
  const isFromPartner = useAppSelector((state) => state.auth.fromToken)
  const availablePrograms = useAppSelector(selectAvailablePrograms())
  const { multiProgramFlowProgramId } = useMultiProgramFlowControl()
  const flowProgram = useAppSelector(
    selectProgram(multiProgramFlowProgramId ?? -1),
  )

  const programExcludedMakes = match({
    isFromPartner,
    availablePrograms,
    flowProgram,
  })
    .with({ isFromPartner: true }, ({ availablePrograms }) => {
      return (
        availablePrograms[0]?.excluded_vehicle_make_models?.map(
          (makeModel) => makeModel.make,
        ) ?? []
      )
    })
    .with({ flowProgram: P.nonNullable }, ({ flowProgram }) => {
      return (
        flowProgram.utility_program?.excluded_vehicle_make_models?.map(
          (makeModel) => makeModel.make,
        ) ?? []
      )
    })
    .otherwise(() => {
      return []
    })
    .map((make) => make.toLowerCase())

  const processedMakes: ProcessedVehicleMake[] = makes.map((make) => {
    const isInBackSoonMakes =
      make.smartcarMake !== undefined &&
      backSoonBrands
        .map((brand) => brand.toLowerCase())
        .includes(make.smartcarMake?.toLowerCase())
    return {
      ...make,
      backSoon: Boolean(
        fromPartnerOnly
          ? isInBackSoonMakes && isFromPartner
          : isInBackSoonMakes,
      ),
      showApology:
        make.showApology ||
        !stateConfiguredEligibleMakes?.includes(make.id ?? ''),
      hidden: false,
      ineligible: Boolean(programExcludedMakes.includes(make.id)),
    }
  })

  return {
    vehicleMakes: processedMakes,
    isLoading: isLoadingOrIdle || isLoadingEligibleMakes,
  }
}

export type ProcessedVehicleMake = VehicleBrand & {
  backSoon: boolean
  hidden: boolean
  ineligible: boolean
}

export const makes: VehicleBrand[] = [
  {
    id: 'tesla',
    name: 'Tesla',
    logo: '/dashboard/img/brands/tesla.webp',
    smartcarMake: 'TESLA',
  },
  {
    id: 'acura',
    name: 'Acura',
    logo: '/dashboard/img/brands/acura.svg',
    smartcarMake: 'ACURA',
    showApology: true,
  },
  {
    id: 'alfa-romeo',
    name: 'Alfa Romeo',
    logo: '/dashboard/img/brands/alfa_romeo.webp',
    smartcarMake: 'ALFA_ROMEO',
    showApology: false,
  },
  {
    id: 'audi',
    name: 'Audi',
    logo: '/dashboard/img/brands/audi.svg',
    smartcarMake: 'AUDI',
  },
  {
    id: 'bentley',
    name: 'Bentley',
    logo: '/dashboard/img/brands/bentley.svg',
    showApology: true,
  },
  {
    id: 'bmw',
    name: 'BMW',
    logo: '/dashboard/img/brands/bmw.svg',
    smartcarMake: 'BMW',
    showApology: true,
  },
  {
    id: 'cadillac',
    name: 'Cadillac',
    logo: '/dashboard/img/brands/cadillac.svg',
    smartcarMake: 'CADILLAC',
  },
  {
    id: 'chevrolet',
    name: 'Chevrolet',
    logo: '/dashboard/img/brands/chevrolet.svg',
    smartcarMake: 'CHEVROLET',
  },
  {
    id: 'chrysler',
    name: 'Chrysler',
    logo: '/dashboard/img/brands/chrysler.svg',
    smartcarMake: 'CHRYSLER',
  },
  {
    id: 'dodge',
    name: 'Dodge',
    logo: '/dashboard/img/brands/dodge.svg',
    smartcarMake: 'DODGE',
    showApology: false,
  },
  {
    id: 'ferrari',
    name: 'Ferrari',
    logo: '/dashboard/img/brands/ferrari.svg',
    showApology: true,
  },
  {
    id: 'fiat',
    name: 'Fiat',
    logo: '/dashboard/img/brands/fiat.svg',
    smartcarMake: 'FIAT',
    showApology: false,
  },
  {
    id: 'ford',
    name: 'Ford',
    logo: '/dashboard/img/brands/ford.svg',
    smartcarMake: 'FORD',
    showApology: true,
  },
  {
    id: 'genesis',
    name: 'Genesis',
    logo: '/dashboard/img/brands/genesis.svg',
    showApology: true,
  },
  {
    id: 'gmc',
    name: 'GMC',
    logo: '/dashboard/img/brands/gmc.svg',
    smartcarMake: 'GMC',
  },
  {
    id: 'honda',
    name: 'Honda',
    logo: '/dashboard/img/brands/honda.svg',
    smartcarMake: 'HONDA',
    showApology: true,
  },
  {
    id: 'hyundai',
    name: 'Hyundai',
    logo: '/dashboard/img/brands/hyundai.svg',
    smartcarMake: 'HYUNDAI',
  },
  {
    id: 'jaguar',
    name: 'Jaguar',
    logo: '/dashboard/img/brands/jaguar.svg',
    smartcarMake: 'JAGUAR',
  },
  {
    id: 'jeep',
    name: 'Jeep',
    logo: '/dashboard/img/brands/jeep.svg',
    smartcarMake: 'JEEP',
  },
  {
    id: 'kandi',
    name: 'Kandi',
    logo: '/dashboard/img/brands/kandi.jpg',
    showApology: true,
  },
  {
    id: 'kia',
    name: 'Kia',
    logo: '/dashboard/img/brands/kia.svg',
    smartcarMake: 'KIA',
  },
  {
    id: 'land-rover',
    name: 'Land Rover',
    logo: '/dashboard/img/brands/land_rover.svg',
    smartcarMake: 'LAND_ROVER',
  },
  {
    id: 'lexus',
    name: 'Lexus',
    logo: '/dashboard/img/brands/lexus.svg',
    smartcarMake: 'LEXUS',
  },
  {
    id: 'lincoln',
    name: 'Lincoln',
    logo: '/dashboard/img/brands/lincoln.svg',
    smartcarMake: 'LINCOLN',
  },
  {
    id: 'lucid',
    name: 'Lucid',
    logo: '/dashboard/img/brands/lucid.svg',
    showApology: true,
  },
  {
    id: 'maserati',
    name: 'Maserati',
    logo: '/dashboard/img/brands/maserati.svg',
    showApology: true,
  },
  {
    id: 'mazda',
    name: 'Mazda',
    logo: '/dashboard/img/brands/mazda.svg',
    smartcarMake: 'MAZDA',
    showApology: false,
  },
  {
    id: 'mclaren-automotive',
    name: 'McLaren Automotive',
    logo: '/dashboard/img/brands/mclaren_automotive.svg',
    showApology: true,
  },
  {
    id: 'mercedes-benz',
    name: 'Mercedes-Benz',
    logo: '/dashboard/img/brands/mercedes_benz.svg',
    smartcarMake: 'MERCEDES-BENZ',
    showApology: false,
  },
  {
    id: 'mini',
    name: 'Mini',
    logo: '/dashboard/img/brands/mini.svg',
    smartcarMake: 'MINI',
    showApology: true,
  },
  {
    id: 'mitsubishi',
    name: 'Mitsubishi',
    logo: '/dashboard/img/brands/mitsubishi.svg',
    showApology: true,
  },
  {
    id: 'nissan',
    name: 'Nissan',
    logo: '/dashboard/img/brands/nissan.svg',
    smartcarMake: 'NISSAN',
    showApology: true,
  },
  {
    id: 'polestar',
    name: 'Polestar',
    logo: '/dashboard/img/brands/polestar.svg',
    showApology: true,
  },
  {
    id: 'porsche',
    name: 'Porsche',
    logo: '/dashboard/img/brands/porsche.svg',
    smartcarMake: 'PORSCHE',
  },
  {
    id: 'ram',
    name: 'Ram',
    logo: '/dashboard/img/brands/ram.png',
    smartcarMake: 'RAM',
  },
  {
    id: 'rivian',
    name: 'Rivian',
    logo: '/dashboard/img/brands/rivian.svg',
    smartcarMake: 'RIVIAN',
  },
  {
    id: 'rolls-royce',
    name: 'Rolls-Royce',
    logo: '/dashboard/img/brands/rolls_royce.svg',
    showApology: true,
  },
  {
    id: 'smart',
    name: 'Smart',
    logo: '/dashboard/img/brands/smart.svg',
    showApology: true,
  },
  {
    id: 'subaru',
    name: 'Subaru',
    logo: '/dashboard/img/brands/subaru.webp',
    smartcarMake: 'SUBARU',
    showApology: false,
  },
  {
    id: 'toyota',
    name: 'Toyota',
    logo: '/dashboard/img/brands/toyota.svg',
    smartcarMake: 'TOYOTA',
  },
  {
    id: 'vinfast',
    name: 'Vinfast',
    logo: '/dashboard/img/brands/vinfast.svg',
    showApology: true,
  },
  {
    id: 'volkswagen',
    name: 'Volkswagen',
    logo: '/dashboard/img/brands/volkswagen.svg',
    smartcarMake: 'VOLKSWAGEN',
  },
  {
    id: 'volvo',
    name: 'Volvo',
    logo: '/dashboard/img/brands/volvo.svg',
    smartcarMake: 'VOLVO',
  },
]
